import classNames from 'classnames';
import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
	useContext,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useShots from '../../contexts/shots/provider';
import useEchoCancellation from '../../hooks/use-echo-cancellation';
import { useAudioSink } from '../../hooks/devices/use-audio-sink';
import type { GraphicInterface } from '../../stores/types/graphics';
import { ROOM_STATES } from '../../stores/reducers/settings';
import { GreenroomState as GS } from '../../stores/types/types';
import { getAnimationName } from '../../utils/custom-graphics';
import { getVideoPoster, isVideo } from '../../utils/utils';
import {
	durationOptions,
	transitionEffects as inEffects,
	transitionOutEffects as outEffects,
} from '../right-panel/settings/animations/types';
import { CanvasLocation } from './custom-graphics-canvas';
import { SocketContext } from '../../contexts/socket';
import usePrevious from '../../utils/use-previous';
import { ContentServiceJobStatus, FkTable } from '../../stores/types/content';
import MediaVideo from '../general-components/media-video';
import StylePackLoadingSpinner from '../style-packs/style-pack-loading-spinner';
import { TABS } from '../../stores/types/tabs';
import useClickAwayListener from '../../hooks/use-click-away-listener';
import { setCurrentGraphicsId } from '../../stores/actions/graphics';
import { EnablerStates } from '../../stores/types/session';

const CUSTOM_GRAPHIC_STATES = {
	INITIAL: 0,	// Initial state
	IN: 1, 		// Animating in or animation is done and should be shown
	OUT: 2, 	// Animating out
	SHOWN: 3, 	// In the edit modal and all overlays are shown
	HIDDEN: 4,	// Hidden state
};

enum TransitionStates {
	None = 'none',
	Manual = 'manual',
	Shot = 'on-shot-transition',
	Delay = 'after-delay',
}

export interface CustomGraphicProps {
	graphic: GraphicInterface;
	selectGraphic: Function;
	location?: CanvasLocation | string;
	isForShotEditorModal?: Boolean;
	isHidden?: boolean;
	isPreloading?: boolean;
	isThumbnail?: boolean;
	// (generated content) graphic being edited, don't replay intro animation
	replayBlocked?: boolean;
	updateGraphicRendered?: (
		graphic: GraphicInterface,
		rendered: boolean,
		isAutoBuildOut: boolean,
	) => void;
	animatingOutCallback?: (id?: number) => void;
	forceUpdateMovable?: () => void;
	updateCurrentGraphicEl: (ref: HTMLDivElement | null) => void;
}

export default function CustomGraphic(
	props: CustomGraphicProps,
) {
	const {
		graphic,
		selectGraphic,
		location = '',
		isForShotEditorModal,
		isHidden = false,
		isPreloading = false,
		isThumbnail = false,
		replayBlocked = false,
		updateGraphicRendered = () => {},
		animatingOutCallback = () => {},
		forceUpdateMovable = () => {},
		updateCurrentGraphicEl,
	} = props;

	const dispatch = useDispatch();
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const currentShotId = useSelector((s: GS) => s.ShotsReducer.currentShotId);
	const activeTab = useSelector((s: GS) => s.TabsReducer.activeTab);
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const globalIsDragging = useSelector((s: GS) => s.UserInterfaceReducer.globalIsDragging);
	const backstageCanvasVolume = useSelector((s: GS) => s.MeetingReducer.backstageCanvasVolume);
	const enablerState = useSelector((s: GS) => s.MeetingReducer.enablerState);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const currentGraphicId = useSelector((s: GS) => s.GraphicsReducer.currentGraphicId);
	const isPreviewAnimationPlaying = useSelector(
		(s: GS) => s.GraphicsReducer.isPreviewAnimationPlaying,
	);
	const shotStartTimestampEntity = useSelector(
		(s: GS) => s.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);

	const [playState, setPlayState] = useState(isThumbnail || isForShotEditorModal
		? CUSTOM_GRAPHIC_STATES.SHOWN
		: CUSTOM_GRAPHIC_STATES.INITIAL);
	const [isAnimating, setIsAnimating] = useState(false);

	const timmy = useRef<NodeJS.Timeout | null>(null);
	const delayTimmy = useRef<NodeJS.Timeout | null>(null);
	const videoEl = useRef<HTMLVideoElement | null>(null);
	const audioEl = useRef<HTMLAudioElement | null>(null);
	const graphicRef = useRef<HTMLDivElement | null>(null);

	const { socket } = useContext(SocketContext);
	const { isShotBuilderOpen, isOnCanvas } = useShots();
	const { applySinkId } = useAudioSink();
	useEchoCancellation(videoEl, audioEl);

	const {
		animation: {
			in_duration: inDurationBase = 0,
			out_duration: outDurationBase = 0,
			build_in: buildIn = 0,
			build_out: buildOut = 0,
			show_for: showForBase = 0,
			delay_in: delayIn = 0,
		},
	} = graphic;

	const inTransition = inEffects[buildIn]?.value ?? TransitionStates.None;
	const outTransition = outEffects[buildOut]?.value ?? TransitionStates.None;
	const inDuration = durationOptions[inDurationBase]?.value ?? 0;
	const outDuration = durationOptions[outDurationBase]?.value ?? 0;
	const showFor = outTransition === TransitionStates.Delay
		? showForBase
		: Infinity;

	const [previewImage, setPreviewImage] = useState('');
	const [previewXScale, setPreviewXScale] = useState(0);
	const prevPreviewImage = usePrevious(previewImage);
	const [showImageAfterVideo, setShowImageAfterVideo] = useState(false);
	const prevCurrentGraphicId = usePrevious(currentGraphicId);

	const showLoadingState = useMemo(() => {
		return (roomState === ROOM_STATES.EDITING || !!isForShotEditorModal) && enablerState === EnablerStates.None;
	}, [roomState, isForShotEditorModal, enablerState]);

	const unsetPreview = () => {
		socket?.connection.off(`content-thumb-update-${graphic.id}${isForShotEditorModal ? '-draft' : ''}`);
		setPreviewImage('');
		setPreviewXScale(0);
	};

	const previewListener = () => {
		if (!showLoadingState) return;
		socket?.connection.on(
			`content-thumb-update-${graphic.id}${isForShotEditorModal ? '-draft' : ''}`,
			(e: { url: string, newXScale: number }) => {
				setPreviewImage(e.url);
				setPreviewXScale(e.newXScale);
				setShowImageAfterVideo(false);
			},
		);
	};

	useEffect(() => {
		graphic.convert_status === ContentServiceJobStatus.processing
			? previewListener()
			: unsetPreview();

		return () => unsetPreview();
	}, [graphic.convert_status, graphic.id]);

	const xScaleToUse = previewImage ? previewXScale : graphic.scale_x;

	useEffect(() => {
		// update target of box when scale is updated via load (for edit modal)
		if (currentGraphicId === graphic.id) forceUpdateMovable();
	}, [currentGraphicId, graphic.id, xScaleToUse, graphic.scale_y]);

	// When component will unmount
	useEffect(() => () => {
		// Clean up timeouts
		if (timmy.current) {
			clearTimeout(timmy.current);
		}

		if (delayTimmy.current) {
			clearTimeout(delayTimmy.current);
		}

		// Clean up video overlays
		if (videoEl.current) {
			videoEl.current.pause();
			videoEl.current.removeAttribute('src');
			videoEl.current.load();
		}
	}, []);

	// Apply setSinkId to video and audio elements
	useEffect(() => {
		if (videoEl.current) {
			applySinkId(videoEl.current);
		}
		if (audioEl.current) {
			applySinkId(audioEl.current);
		}
	}, [applySinkId, videoEl.current, audioEl.current]);

	// this function is for on delay transition
	const doDelayTimer = () => {
		if (roomState !== ROOM_STATES.EDITING) {
			setPlayState(CUSTOM_GRAPHIC_STATES.HIDDEN);
			if (delayTimmy.current) {
				clearTimeout(delayTimmy.current);
			}
			if (shotStartTimestampEntity) {
				// when shot starts, don't animate until delay expires
				setIsAnimating(false);
				delayTimmy.current = setTimeout(() => {
					setPlayState(CUSTOM_GRAPHIC_STATES.IN);
					delayTimmy.current = null;
				}, delayIn * 1000);
			}
		} else if (!isHidden) {
			setPlayState(CUSTOM_GRAPHIC_STATES.SHOWN);
		} else if (isHidden) {
			if (timmy.current) {
				setIsAnimating(false);
				clearTimeout(timmy.current);
			}
			setPlayState(CUSTOM_GRAPHIC_STATES.HIDDEN);
		}
	};

	const getCurrentAnimationName = (): string => {
		if (
			enablerState !== EnablerStates.Playing 
			&& (
				isForShotEditorModal
				|| isThumbnail
				|| (roomState === ROOM_STATES.EDITING && playState !== CUSTOM_GRAPHIC_STATES.OUT)
			)
		) {
			return '';
		}
		return getAnimationName(graphic, playState === CUSTOM_GRAPHIC_STATES.OUT);
	};

	const initialStyle = useMemo(() => ({
		top: `${graphic.pos_y}%`,
		left: `${graphic.pos_x}%`,
		width: `${xScaleToUse}%`,
		height: `${graphic.scale_y}%`,
	}), [graphic.pos_y, graphic.pos_x, xScaleToUse, graphic.scale_y]);

	useEffect(() => {
		if (roomState === ROOM_STATES.EDITING && isHidden) {
			setPlayState(CUSTOM_GRAPHIC_STATES.HIDDEN);
		} else if (isForShotEditorModal) {
			setPlayState(CUSTOM_GRAPHIC_STATES.SHOWN);
		} else if (playState === CUSTOM_GRAPHIC_STATES.INITIAL) {
			if (
				(isHidden && !isThumbnail)
				|| (
					(
						inTransition === TransitionStates.Manual
						|| inTransition === TransitionStates.Shot
					)
					&& !shotStartTimestampEntity
				)
			) {
				// This may end up overridden by setPlayState() in another useEffect
				setPlayState(CUSTOM_GRAPHIC_STATES.HIDDEN);
			} else if (inTransition === TransitionStates.Delay) {
				if (shotStartTimestampEntity) {
					doDelayTimer();
				}
			} else if (!isHidden) {
				setPlayState(CUSTOM_GRAPHIC_STATES.IN);
			}
		} else if (playState === CUSTOM_GRAPHIC_STATES.HIDDEN
			&& inTransition === TransitionStates.Delay
		) {
			if (shotStartTimestampEntity && !isThumbnail && !isHidden) {
				doDelayTimer();
			}
		}
	}, [roomState, shotStartTimestampEntity]);

	useEffect(() => {
		if (
			inTransition === TransitionStates.Delay
			&& isHidden
			&& delayTimmy.current
		) {
			clearTimeout(delayTimmy.current);
		}
		// Videos that do not loop are having a problem currently showing again this fixes it
		if (
			!isHidden
			&& isVideo(graphic.images)
			&& playState === CUSTOM_GRAPHIC_STATES.HIDDEN
		) {
			setPlayState(CUSTOM_GRAPHIC_STATES.INITIAL);
		}

		// Reset showImageAfterVideo when component becomes hidden or changes state
		if (isHidden || playState === CUSTOM_GRAPHIC_STATES.INITIAL) {
			setShowImageAfterVideo(false);
		}
	}, [isHidden, playState]);

	useEffect(() => {
		// TODO: I don't know how to tell if the shot is playing
		if (
			enablerState === EnablerStates.Playing
			|| (currentShotId !== null && shotStartTimestampEntity !== null) 
		) {
			switch (inTransition) {
				case TransitionStates.Shot:
				case TransitionStates.Manual:
					if (!isHidden) {
						setPlayState(CUSTOM_GRAPHIC_STATES.IN);
					} else if (!isThumbnail) {
						setPlayState(CUSTOM_GRAPHIC_STATES.HIDDEN);
					}
					break;
				default:
					break;
			}
		}
		// TODO: serverState should become the currentShot and determine if it is playing
	}, [shotStartTimestampEntity, enablerState]);

	useEffect(() => {
		// If true, graphic persists on screen until it is manually hidden
		const isManualBuildOut = showFor === Infinity;

		// There is no animation during SHOWN, at least in the editor and shot modal
		if ((roomState === ROOM_STATES.EDITING || isForShotEditorModal)
			&& playState === CUSTOM_GRAPHIC_STATES.SHOWN
		) {
			if (timmy.current) {
				clearTimeout(timmy.current);
				timmy.current = null;
			}
			if (isAnimating) setIsAnimating(false);
			return;
		}

		if (!isManualBuildOut
			&& playState !== CUSTOM_GRAPHIC_STATES.OUT
			&& playState !== CUSTOM_GRAPHIC_STATES.IN
		) {
			// existing timer would be allowed to run in this case according to original logic
			return;
		}

		if (timmy.current) {
			clearTimeout(timmy.current);
			timmy.current = null;
		}

		// Animating in, will auto build out
		if (!isManualBuildOut && playState === CUSTOM_GRAPHIC_STATES.IN) {
			setIsAnimating(inTransition !== TransitionStates.None);
			timmy.current = setTimeout(() => {
				timmy.current = null;
				setPlayState(CUSTOM_GRAPHIC_STATES.OUT);
				setIsAnimating(false);
			}, (inDuration + showFor) * 1000);
		}

		// Animating out to be hidden
		if (playState === CUSTOM_GRAPHIC_STATES.OUT) {
			setIsAnimating(outTransition !== TransitionStates.None);
			timmy.current = setTimeout(() => {
				timmy.current = null;
				setPlayState(CUSTOM_GRAPHIC_STATES.HIDDEN);
				setIsAnimating(false);
				// Actions to take when animating out
				animatingOutCallback(graphic.id);
			}, (outDuration * 1000) - (isManualBuildOut ? 10 : 0));
		}

		// Animating in (or when SHOWN outside editing room), will persist
		if (isManualBuildOut
			&& (playState === CUSTOM_GRAPHIC_STATES.IN || playState === CUSTOM_GRAPHIC_STATES.SHOWN)
		) {
			setIsAnimating(inTransition !== TransitionStates.None);
			timmy.current = setTimeout(() => {
				timmy.current = null;
				setIsAnimating(false);
			}, (inDuration * 1000) - 10);
		}

		// Is manually hidden (or specifically manual build out and INITIAL, unsure why)
		if (isManualBuildOut
			&& (playState === CUSTOM_GRAPHIC_STATES.INITIAL || playState === CUSTOM_GRAPHIC_STATES.HIDDEN)
		) {
			setIsAnimating(false);
		}
	}, [playState, shotStartTimestampEntity]);

	useEffect(() => {
		// this component is hidden, and not supposed to be shown
		if (isHidden && !isShotBuilderOpen && !isThumbnail) {
			// we need to animate out
			if ([CUSTOM_GRAPHIC_STATES.IN, CUSTOM_GRAPHIC_STATES.SHOWN].includes(playState)
				&& outDuration > 0
			) {
				setPlayState(CUSTOM_GRAPHIC_STATES.OUT);
			} else { // just hide it
				setPlayState(CUSTOM_GRAPHIC_STATES.HIDDEN);
			}
		} else if (inTransition === TransitionStates.Delay) {
			if (isThumbnail) {
				setPlayState(CUSTOM_GRAPHIC_STATES.SHOWN);
			} else {
				doDelayTimer();
			}
		} else {
			const isBuildInBypassed = isThumbnail
				|| isForShotEditorModal
				|| (
					roomState === ROOM_STATES.EDITING
					&& playState !== CUSTOM_GRAPHIC_STATES.SHOWN
				);
			setPlayState(
				isBuildInBypassed
					? CUSTOM_GRAPHIC_STATES.SHOWN
					: CUSTOM_GRAPHIC_STATES.IN,
			);
		}
	}, [isHidden]);

	useEffect(() => {
		// this component is hidden, and not supposed to be shown
		const hidden = isHidden && !isShotBuilderOpen && !isThumbnail;
		if (
			!hidden
			&& !replayBlocked
			&& videoEl.current
			&& videoEl.current.readyState // wait for the video to be ready
			&& (
				videoEl.current.paused
				|| videoEl.current.currentTime !== 0
			)
		) {
			videoEl.current.currentTime = 0;
			videoEl.current.play();
			if (audioEl.current && !embed && !isOnCanvas && roomState !== ROOM_STATES.EDITING) {
				audioEl.current.volume = backstageCanvasVolume;
			}
		}
	}, [isHidden, videoEl.current, replayBlocked]);

	useEffect(() => {
		if (
			graphic.loops
			&& videoEl.current
			&& videoEl.current.readyState // wait for the video to be ready
			&& videoEl.current.paused
		) {
			videoEl.current.play();
			if (audioEl.current && !embed) {
				audioEl.current.volume = (isOnCanvas || roomState === ROOM_STATES.EDITING)
					? 1
					: backstageCanvasVolume;
			}
		}
	}, [graphic, videoEl.current]);

	useEffect(
		() => {
			if ((audioEl.current && videoEl.current) && !embed) {
				audioEl.current.volume = (isOnCanvas || roomState === ROOM_STATES.EDITING)
					? 1
					: backstageCanvasVolume;
			}
		},
		[
			backstageCanvasVolume,
			isOnCanvas,
			roomState,
			audioEl.current,
			videoEl.current,
		],
	);

	const shouldRender = useMemo(() => {
		// preloading next shot: render in DOM so browser loads images/videos
		if (isPreloading) return true;

		// if is a thumbnail always show
		if (isThumbnail) return true;

		// if enabler is playing, render as if in live mode
		if (enablerState !== EnablerStates.Playing) {
			// show always in edit modal or in show builder
			if (isForShotEditorModal || (roomState === ROOM_STATES.EDITING && !isHidden)) return true;

			// allows to hide in show builder
			if (roomState === ROOM_STATES.EDITING && isHidden) {
				return false;
			}
		}

		// Using vars to make logic easier to understand
		// based on room state can we show
		const canShowWithState = (
			roomState === ROOM_STATES.SHOWTIME
				&& shotStartTimestampEntity
		) || roomState === ROOM_STATES.EDITING;

		// these are the states where a graphic can be shown
		const isInShownState = [
			CUSTOM_GRAPHIC_STATES.IN,
			CUSTOM_GRAPHIC_STATES.OUT,
			CUSTOM_GRAPHIC_STATES.SHOWN,
		].includes(playState);

		if (graphic.images && canShowWithState && isInShownState) {
			return true;
		}

		return false;
	}, [graphic.images,
		isForShotEditorModal,
		isPreloading,
		isThumbnail,
		isHidden,
		roomState,
		shotStartTimestampEntity,
		playState,
		enablerState,
	]);

	function handleGraphicClicked() {
		if (!isAnimating && host) {
			selectGraphic(graphic);
		}
	}

	useClickAwayListener({
		ref: graphicRef,
		onClickAway: (event: MouseEvent) => {
			if (!isAnimating && host && !isThumbnail) {
				const mainVideoContainer = document.querySelector('.main-video-container');
				const target = event.target as HTMLElement;
				const isSibling = graphicRef.current?.parentElement === target.parentElement

				if (mainVideoContainer && mainVideoContainer?.contains(event.target as Node) &&
					!isSibling && !target.classList.contains('media-video-container') &&
					!target.classList.contains('custom-graphics-child')) {
					selectGraphic(null);
					dispatch(setCurrentGraphicsId(0));
					updateCurrentGraphicEl(null);
				}
			}
		},
		open: true,
	});

	useEffect(() => {
		if (graphic?.id) {
			const autoBuildOut: boolean = outTransition === TransitionStates.Delay;

			updateGraphicRendered(graphic, shouldRender, autoBuildOut);
		}
	}, [graphic, shouldRender]);

	useEffect(() => {
		if (currentGraphicId === graphic.id) {
			updateCurrentGraphicEl(graphicRef.current);
		} else if (prevCurrentGraphicId === graphic.id && currentGraphicId !== graphic.id) {
			// Only reset when clicking away from this specific graphic
			setShowImageAfterVideo(false);
		}
	}, [currentGraphicId, graphic.id, prevCurrentGraphicId]);

	const [wrapRotate, childRotate] = useMemo(() => {
		// I believe this is for when someone clicks the shot tab build in/out buttons
		// (as opposed to the preview button in the animation drawer)
		const isEditingNotPreview = (
			roomState === ROOM_STATES.EDITING
			&& activeTab !== TABS.OVERLAYS
		);

		let shouldWrapParent = false;
		if (playState === CUSTOM_GRAPHIC_STATES.SHOWN && !isEditingNotPreview) {
			// editing, so moveable (in canvas component) needs the rotation attached to the wrapper
			shouldWrapParent = true;
		}

		if (roomState === ROOM_STATES.EDITING) {
			// if animating, wrapper has a transform that overwrites rotation
			// in editor mode, this can trigger from preview or shot tab button
			shouldWrapParent = !isPreviewAnimationPlaying;
		}

		if (isForShotEditorModal && isPreviewAnimationPlaying) {
			// if in shot editor, animating overlays do not get isAnimating set to true
			// so use previewing an animation from the animation drawer flag to
			// wrap the child, since the parent will have an overwriting animation
			shouldWrapParent = false;
		}

		const returnedStrings = shouldWrapParent
			? [`rotate(${graphic.rotate}deg)`, '']
			: ['', `rotate(${graphic.rotate}deg)`];

		return returnedStrings;
	}, [graphic.rotate,
		graphic.id,
		outTransition,
		inTransition,
		isPreviewAnimationPlaying,
		playState,
		roomState,
		isAnimating,
	]);

	const hiddenCSS = useMemo(() => {
		if (isPreloading) return true;
		return location === CanvasLocation.ShotEditor
			|| (!isHidden && roomState === ROOM_STATES.EDITING)
			? false
			: playState === CUSTOM_GRAPHIC_STATES.HIDDEN && !isThumbnail;
	}, [location, isHidden, isPreloading, roomState, playState, isThumbnail]);

	const doRenderVideo = useMemo(
		() => isVideo(graphic.images) && !isThumbnail && !previewImage && !showImageAfterVideo,
		[graphic.images, isThumbnail, previewImage, showImageAfterVideo],
	);

	const isHlsVideo = useMemo((): boolean => graphic.images?.endsWith('.m3u8'), [graphic.images]);

	if (!shouldRender) {
		return (<></>);
	}

	return (<div
		id={`graphic-wrapper-${graphic.id}${location ? `-${location}` : ''}`}
		data-testid={`graphic-wrapper-${graphic.id}`}
		className={classNames(
			'graphic-wrapper',
			getCurrentAnimationName(),
			{
				'pointer-events-none': globalIsDragging || !host,
				hidden: hiddenCSS,
			},
		)}
		style={{ ...initialStyle, transform: wrapRotate }}
		onClick={handleGraphicClicked}
		ref={graphicRef}
	>

		<div
			className="custom-graphics-child"
			id={`custom-graphic-${graphic.id}${location ? `-${location}` : ''}`}
			style={{ transform: childRotate }}
		>
			{doRenderVideo
				? <>
					<MediaVideo
						data-testid={`videoref_${graphic.id}`}
						id={`custom-graphic-vid-${graphic.id}${location ? `-${location}` : ''}`}
						ref={videoEl}
						src={graphic.images}
						isHlsVideo={isHlsVideo}
						duration={videoEl.current?.duration ? videoEl.current?.duration : 0}
						elapsed={videoEl.current?.currentTime ? videoEl.current?.currentTime : 0}
						muted={(graphic.play_audio !== 1 && graphic.play_audio !== true)}
						crossOrigin="anonymous"
						loop={typeof graphic.loops === 'number' ? Boolean(graphic?.loops) : false}
						posterImage={graphic.provider === 'capsule' ? '' : getVideoPoster(graphic.images)}
						autoPlay={!isPreloading}
						onLoadedData={() => {
							// preview image was reset because video loaded,
							// go to end frame (which is hopefully the preview image)
							if (prevPreviewImage && videoEl.current) {
								videoEl.current.currentTime = 15;
							}
						}}
						onEnded={() => {
							// Show preview image when video ends
							// but only in editing room or shot editor modal
							// (otherwise the graphic would never disappear)
							if (
								enablerState !== EnablerStates.Playing // not if enabler is playing
								&& (roomState === ROOM_STATES.EDITING || isForShotEditorModal)
							) {
								setTimeout(() => {
									setShowImageAfterVideo(true);
								}, 2000);
							}
						}}
					/>
					<audio
						data-testid={`audioref_${graphic.id}`}
						ref={audioEl}
						muted={(graphic.play_audio !== 1 && graphic.play_audio !== true)}
					></audio>
				</>
				: <img
					data-testid={`imageref_${graphic.id}`}
					alt="custom graphic"
					src={previewImage || graphic.thumbnail_url}
					className={showImageAfterVideo ? 'image-after-video-fade-in' : ''}
				/>
			}
		</div>
		{isThumbnail || !showLoadingState ? null : <StylePackLoadingSpinner
			type={FkTable.graphics}
			id={graphic.id ?? 0}
			isForShotEditorModal={!!isForShotEditorModal}
		/>}
	</div>);
}
